import { IndicatorResponseDto } from '../dto/response/indicator-response.dto';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import indicatorService from '../services/indicator.service';
import { IndicatorSaveRequestDto } from '../dto/request/indicator-save-request.dto';
import { IndicatorUpdateRequestDto } from '../dto/request/indicator-update-request.dto';
import { AxiosError } from 'axios';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { LocalesLanguagesConstants } from '../../../../locales/locales';

const initialState = {
  status: 'idle',
  indicators: [] as IndicatorResponseDto[] | null,
  indicator: {} as IndicatorResponseDto | null,
  error: null as string | null,
};

export const fetchIndicators = createAsyncThunk(
  'indicators/fetchIndicators',
  async () => {
    return await indicatorService.fetchIndicators();
  }
);

export const fetchIndicatorById = createAsyncThunk(
  'indicators/fetchIndicatorById',
  async (params: { id: string; language: LocalesLanguagesConstants }) => {
    return await indicatorService.fetchIndicatorById(
      params.id,
      params.language
    );
  }
);

export const saveIndicator = createAsyncThunk(
  'indicators/saveIndicator',
  async (params: {
    saveRequest: IndicatorSaveRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await indicatorService.saveIndicator(
      params.saveRequest,
      params.language
    );
  }
);

export const updateIndicator = createAsyncThunk(
  'indicators/updateIndicator',
  async (params: {
    updateRequest: IndicatorUpdateRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await indicatorService.updateIndicator(
      params.updateRequest,
      params.language
    );
  }
);

export const deleteIndicator = createAsyncThunk(
  'indicators/deleteIndicator',
  async (
    payload: { existingId: string; compactId: string | null },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await indicatorService.deleteIndicator(
        payload.existingId,
        payload.compactId
      );
      dispatch(fetchIndicators());
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const indicatorSlice = createSlice({
  name: 'indicatorSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchIndicators.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchIndicators.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.indicators = action.payload;
      })
      .addCase(fetchIndicators.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchIndicatorById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchIndicatorById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.indicator = action.payload;
      })
      .addCase(fetchIndicatorById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(saveIndicator.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveIndicator.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.indicators?.push(action.payload);
        showSuccess('Indicator saved successfully.');
      })
      .addCase(saveIndicator.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(updateIndicator.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateIndicator.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.indicators) {
          const indicatorIndex = state.indicators.findIndex(
            (indicator) => indicator.id === action.payload.id
          );
          if (indicatorIndex !== -1) {
            state.indicators[indicatorIndex] = action.payload;
          }
        }
      })
      .addCase(updateIndicator.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteIndicator.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteIndicator.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Indicator deleted successfully.');
      })
      .addCase(deleteIndicator.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting indicator failed. ${action.payload as string}`);
      });
  },
});

export default indicatorSlice.reducer;
